(() => {
  if (customElements.get('codex-aassort-size-guide')) return;

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  const FURNITURE = {
    sofa: { width: 84, height: 32, minX: 10, maxX: 110, minY: 23, maxY: 83 },
    bed: { width: 62, height: 48, minX: 11, maxX: 109, minY: 16, maxY: 83 },
    wall: { width: 96, height: 0, minX: 0, maxX: 120, minY: 76, maxY: 82 },
    console: { width: 48, height: 32, minX: 12, maxX: 108, minY: 20, maxY: 77 },
    dining: { width: 72, height: 30, minX: 12, maxX: 108, minY: 31, maxY: 79 },
    fireplace: { width: 54, height: 50, minX: 10, maxX: 110, minY: 18, maxY: 76 },
    desk: { width: 55, height: 30, minX: 10, maxX: 110, minY: 15, maxY: 77 },
    bench: { width: 48, height: 18, minX: 12, maxX: 108, minY: 29, maxY: 81 }
  };

  class CodexAassortSizeGuide extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      this.trigger = document.querySelector('[data-codex-size-guide-trigger="' + this.dataset.triggerId + '"]');
      this.dialog = this.querySelector('.codex-size-guide__dialog');
      this.visual = this.querySelector('[data-size-guide-visual]');
      this.wall = this.querySelector('.codex-size-guide__wall');
      this.scene = this.querySelector('[data-size-guide-scene]');
      this.art = this.querySelector('[data-size-guide-art]');
      this.artShadow = this.querySelector('[data-size-guide-art-shadow]');
      this.artFrame = this.querySelector('[data-size-guide-art-frame]');
      this.artFill = this.querySelector('[data-size-guide-art-fill]');
      this.artPatternFill = this.artFill?.getAttribute('fill') || 'none';
      this.artWidthLine = this.querySelector('[data-size-guide-art-width-line]');
      this.artWidthTickStart = this.querySelector('[data-size-guide-art-width-tick-start]');
      this.artWidthTickEnd = this.querySelector('[data-size-guide-art-width-tick-end]');
      this.artWidthText = this.querySelector('[data-size-guide-art-width-text]');
      this.artHeightLine = this.querySelector('[data-size-guide-art-height-line]');
      this.artHeightTickStart = this.querySelector('[data-size-guide-art-height-tick-start]');
      this.artHeightTickEnd = this.querySelector('[data-size-guide-art-height-tick-end]');
      this.artHeightText = this.querySelector('[data-size-guide-art-height-text]');
      this.furnitureWidthLine = this.querySelector('[data-size-guide-furniture-width-line]');
      this.furnitureWidthTickStart = this.querySelector('[data-size-guide-furniture-width-tick-start]');
      this.furnitureWidthTickEnd = this.querySelector('[data-size-guide-furniture-width-tick-end]');
      this.furnitureWidthText = this.querySelector('[data-size-guide-furniture-width-text]');
      this.caption = this.querySelector('[data-size-guide-caption]');
      this.widthInput = this.querySelector('[data-size-guide-width]');
      this.widthLabel = this.querySelector('[data-size-guide-width-label]');
      this.sizeSelect = this.querySelector('[data-size-guide-size-select]');
      this.recommended = this.querySelector('[data-size-guide-recommended]');
      this.format = this.querySelector('[data-size-guide-format]');
      this.furnitureType = 'sofa';
      this.lastFocus = null;

      const configNode = this.querySelector('[data-size-guide-config]');
      try {
        this.config = JSON.parse(configNode ? configNode.textContent : '{}');
      } catch (error) {
        this.config = {};
      }

      this.sizes = (Array.isArray(this.config.sizes) ? this.config.sizes : [])
        .map((label) => this.parseSize(String(label)))
        .filter(Boolean);

      this.onTriggerClick = () => this.open();
      this.onKeyDown = (event) => this.handleKeyDown(event);
      this.onResize = () => this.render();

      if (this.trigger) this.trigger.addEventListener('click', this.onTriggerClick);
      this.querySelectorAll('[data-codex-size-guide-close]').forEach((button) => {
        button.addEventListener('click', () => this.close());
      });

      this.querySelectorAll('[data-size-guide-furniture-choice]').forEach((button) => {
        button.addEventListener('click', () => {
          this.furnitureType = button.dataset.sizeGuideFurnitureChoice;
          const furniture = FURNITURE[this.furnitureType];
          if (this.widthInput && furniture) {
            this.widthInput.value = String(furniture.width);
          }
          this.querySelectorAll('[data-size-guide-furniture-choice]').forEach((choice) => {
            const active = choice === button;
            choice.classList.toggle('is-active', active);
            choice.setAttribute('aria-pressed', active ? 'true' : 'false');
          });
          this.querySelectorAll('[data-size-guide-furniture]').forEach((item) => {
            const active = item.dataset.sizeGuideFurniture === this.furnitureType;
            item.toggleAttribute('hidden', !active);
            item.style.display = active ? '' : 'none';
          });
          this.render();
        });
      });

      if (this.widthInput) {
        this.widthInput.addEventListener('input', () => this.render());
      }
      this.querySelector('[data-size-guide-width-minus]')?.addEventListener('click', () => this.changeWidth(-1));
      this.querySelector('[data-size-guide-width-plus]')?.addEventListener('click', () => this.changeWidth(1));
      this.sizeSelect?.addEventListener('change', () => this.render());

      document.addEventListener('keydown', this.onKeyDown);
      this.resizeObserver = 'ResizeObserver' in window ? new ResizeObserver(this.onResize) : null;
      if (this.resizeObserver && this.visual) this.resizeObserver.observe(this.visual);

      this.querySelectorAll('[data-size-guide-furniture]').forEach((item) => {
        const active = item.dataset.sizeGuideFurniture === this.furnitureType;
        item.toggleAttribute('hidden', !active);
        item.style.display = active ? '' : 'none';
      });
    }

    parseSize(label) {
      const normalized = label
        .replace(/[\u201C\u201D\u2033]/g, '"')
        .replace(/[\u2018\u2019\u2032\u02BC']{1,2}/g, '"')
        .replace(/[\u00D7]/g, 'x')
        .replace(/\s+/g, ' ')
        .trim();

      const panelMatch = normalized.match(/^\s*(\d+)\s*P\b/i);
      const panels = panelMatch ? Math.max(1, Number(panelMatch[1])) : 1;
      let match = normalized.match(/(\d+(?:\.\d+)?)\s*"?\s*H\s*x\s*(\d+(?:\.\d+)?)\s*"?\s*W/i);
      if (match) {
        return {
          label,
          width: Number(match[2]),
          height: Number(match[1]),
          panels,
          panelGap: panels > 1 ? 2 : 0
        };
      }

      match = normalized.match(/(\d+(?:\.\d+)?)\s*"?\s*W\s*x\s*(\d+(?:\.\d+)?)\s*"?\s*H/i);
      if (match) {
        return {
          label,
          width: Number(match[1]),
          height: Number(match[2]),
          panels,
          panelGap: panels > 1 ? 2 : 0
        };
      }

      match = normalized.match(/(\d+(?:\.\d+)?)\s*(?:"|in(?:ches?)?)?\s*x\s*(\d+(?:\.\d+)?)\s*(?:"|in(?:ches?)?)?/i);
      if (!match) {
        match = normalized.match(/(\d+(?:\.\d+)?)\s*x\s*(\d+(?:\.\d+)?)\s*cm/i);
        if (!match) return null;
        let width = Number(match[1]) / 2.54;
        let height = Number(match[2]) / 2.54;
        if (this.config.swapAmbiguousDimensions && width !== height) {
          [width, height] = [height, width];
        }
        return {
          label,
          width,
          height,
          panels,
          panelGap: panels > 1 ? 2 : 0
        };
      }

      let width = Number(match[1]);
      let height = Number(match[2]);
      const beforeMatch = normalized.slice(0, match.index + match[0].length);
      if (/cm/i.test(beforeMatch) && !/"/.test(match[0]) && !/in/i.test(match[0])) {
        width /= 2.54;
        height /= 2.54;
      }
      if (this.config.swapAmbiguousDimensions && width !== height) {
        [width, height] = [height, width];
      }

      return {
        label,
        width,
        height,
        panels,
        panelGap: panels > 1 ? 2 : 0
      };
    }

    syncWithProductSelection() {
      if (!this.trigger || !this.sizeSelect) return;
      const optionRoot = this.trigger.closest('fieldset, .product-form__input--dropdown');
      if (!optionRoot) return;

      const checked = optionRoot.querySelector('input[type="radio"]:checked');
      const select = optionRoot.querySelector('select');
      const value = checked?.value || select?.value;
      if (value && Array.from(this.sizeSelect.options).some((option) => option.value === value)) {
        this.sizeSelect.value = value;
      }
    }

    open() {
      if (!this.dialog) return;
      window.clearTimeout(this._closeTimer);
      this.lastFocus = document.activeElement;
      this.syncWithProductSelection();
      if (this.parentElement !== document.body) {
        document.body.appendChild(this);
      }
      this.hidden = false;
      this.trigger?.setAttribute('aria-expanded', 'true');
      document.body.classList.add('codex-size-guide-open');

      requestAnimationFrame(() => {
        this.classList.add('is-open');
        this.render();
        this.dialog.focus({ preventScroll: true });
      });
    }

    close() {
      if (this.hidden) return;
      this.classList.remove('is-open');
      this.trigger?.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('codex-size-guide-open');
      this._closeTimer = window.setTimeout(() => {
        this.hidden = true;
        if (this.lastFocus && typeof this.lastFocus.focus === 'function') {
          this.lastFocus.focus({ preventScroll: true });
        }
      }, 240);
    }

    handleKeyDown(event) {
      if (this.hidden) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;

      const focusable = Array.from(this.dialog.querySelectorAll(
        'button:not([disabled]), select:not([disabled]), input:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'
      )).filter((element) => element.offsetParent !== null);

      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    changeWidth(direction) {
      if (!this.widthInput) return;
      const next = clamp(
        Number(this.widthInput.value) + direction,
        Number(this.widthInput.min),
        Number(this.widthInput.max)
      );
      this.widthInput.value = String(next);
      this.render();
    }

    currentSize() {
      const value = this.sizeSelect?.value;
      return this.sizes.find((size) => size.label === value) || this.sizes[0] || {
        label: 'Artwork',
        width: 36,
        height: 36,
        panels: 1,
        panelGap: 0
      };
    }

    compositionWidth(size) {
      const panels = Math.max(1, Number(size.panels) || 1);
      const gap = panels > 1 ? Math.max(0, Number(size.panelGap) || 0) : 0;
      return size.width * panels + gap * (panels - 1);
    }

    suggestedSize(furnitureWidth) {
      if (!this.sizes.length) return null;
      const coverage = ['console', 'desk', 'bench'].includes(this.furnitureType)
        ? .78
        : ['bed', 'fireplace'].includes(this.furnitureType)
          ? .72
          : .65;
      const targetWidth = furnitureWidth * coverage;
      return this.sizes.reduce((best, candidate) => {
        return Math.abs(this.compositionWidth(candidate) - targetWidth) < Math.abs(this.compositionWidth(best) - targetWidth) ? candidate : best;
      }, this.sizes[0]);
    }

    render() {
      if (this.hidden || !this.scene || !this.art) return;

      const size = this.currentSize();
      const furnitureWidth = clamp(Number(this.widthInput?.value || 84), 24, 240);
      const sceneWidth = 1000;
      const sceneCenter = sceneWidth / 2;
      const furnitureSpec = FURNITURE[this.furnitureType] || FURNITURE.sofa;
      const isWall = this.furnitureType === 'wall';
      const compositionWidth = this.compositionWidth(size);
      const sceneContentWidth = 720;
      const furnitureBottom = 520;
      const artTopLimit = 30;
      const artFurnitureGap = 52;
      const verticalContentHeight = furnitureBottom - artTopLimit - artFurnitureGap;
      const pixelsPerInch = Math.min(
        sceneContentWidth / Math.max(furnitureWidth, compositionWidth, 1),
        verticalContentHeight / Math.max(furnitureSpec.height + size.height, 1)
      );
      const furnitureDisplayWidth = furnitureWidth * pixelsPerInch;
      const furnitureDisplayHeight = furnitureSpec.height * pixelsPerInch;
      const furnitureX = (sceneWidth - furnitureDisplayWidth) / 2;
      const furnitureTop = furnitureBottom - furnitureDisplayHeight;
      const furnitureScaleX = furnitureDisplayWidth / (furnitureSpec.maxX - furnitureSpec.minX);
      const furnitureScaleY = isWall ? 1 : furnitureDisplayHeight / (furnitureSpec.maxY - furnitureSpec.minY);
      const furnitureTranslateX = furnitureX - furnitureSpec.minX * furnitureScaleX;
      const furnitureTranslateY = isWall
        ? furnitureBottom - 21 - ((furnitureSpec.minY + furnitureSpec.maxY) / 2)
        : furnitureTop - furnitureSpec.minY * furnitureScaleY;

      const panelWidth = size.width * pixelsPerInch;
      const artHeight = size.height * pixelsPerInch;
      const panelGap = (size.panelGap || 0) * pixelsPerInch;
      const artWidth = compositionWidth * pixelsPerInch;
      const artX = sceneCenter - artWidth / 2;
      const artY = furnitureTop - artFurnitureGap - artHeight;
      this.renderArtworkPanels(size, artX, artY, panelWidth, artHeight, panelGap);

      const artWidthMeasureY = artY + artHeight + 17;
      this.setLine(this.artWidthLine, artX, artWidthMeasureY, artX + artWidth, artWidthMeasureY);
      this.setLine(this.artWidthTickStart, artX, artWidthMeasureY - 6, artX, artWidthMeasureY + 6);
      this.setLine(this.artWidthTickEnd, artX + artWidth, artWidthMeasureY - 6, artX + artWidth, artWidthMeasureY + 6);
      const widthText = size.panels > 1
        ? Math.round(compositionWidth) + ' in display W'
        : Math.round(size.width) + ' in W';
      this.setText(this.artWidthText, artX + artWidth / 2, artWidthMeasureY + 23, widthText);

      const artHeightMeasureX = artX - 24;
      this.setLine(this.artHeightLine, artHeightMeasureX, artY, artHeightMeasureX, artY + artHeight);
      this.setLine(this.artHeightTickStart, artHeightMeasureX - 6, artY, artHeightMeasureX + 6, artY);
      this.setLine(this.artHeightTickEnd, artHeightMeasureX - 6, artY + artHeight, artHeightMeasureX + 6, artY + artHeight);
      if (this.artHeightText) {
        const textX = artHeightMeasureX - 16;
        const textY = artY + artHeight / 2;
        this.setText(this.artHeightText, textX, textY, Math.round(size.height) + ' in H');
        this.artHeightText.setAttribute('transform', 'rotate(-90 ' + textX + ' ' + textY + ')');
      }

      this.querySelectorAll('[data-size-guide-furniture]').forEach((item) => {
        const active = item.dataset.sizeGuideFurniture === this.furnitureType;
        item.toggleAttribute('hidden', !active);
        item.style.display = active ? '' : 'none';
        if (active) {
          item.setAttribute('transform', 'translate(' + furnitureTranslateX + ' ' + furnitureTranslateY + ') scale(' + furnitureScaleX + ' ' + furnitureScaleY + ')');
        }
      });

      const furnitureMeasureY = furnitureBottom + 22;
      this.setLine(this.furnitureWidthLine, furnitureX, furnitureMeasureY, furnitureX + furnitureDisplayWidth, furnitureMeasureY);
      this.setLine(this.furnitureWidthTickStart, furnitureX, furnitureMeasureY - 7, furnitureX, furnitureMeasureY + 7);
      this.setLine(this.furnitureWidthTickEnd, furnitureX + furnitureDisplayWidth, furnitureMeasureY - 7, furnitureX + furnitureDisplayWidth, furnitureMeasureY + 7);
      this.setText(this.furnitureWidthText, sceneWidth / 2, furnitureMeasureY + 25, furnitureWidth + ' in W');

      if (this.widthLabel) this.widthLabel.textContent = furnitureWidth + ' in';
      if (this.caption) {
        const furnitureName = this.furnitureType.charAt(0).toUpperCase() + this.furnitureType.slice(1);
        const panelNote = size.panels > 1 ? ' | shown with ' + size.panelGap + ' in gaps' : '';
        this.caption.textContent = size.label + panelNote + ' | ' + furnitureName + ' ' + furnitureWidth + ' in wide';
      }

      const ratio = compositionWidth / Math.max(size.height, 1);
      if (this.format) {
        this.format.textContent = ratio > 1.08 ? 'Landscape' : ratio < .92 ? 'Portrait' : 'Square';
      }

      const suggested = this.suggestedSize(furnitureWidth);
      if (this.recommended) {
        this.recommended.textContent = suggested ? 'Suggested: ' + suggested.label : '';
      }
    }

    renderArtworkPanels(size, artX, artY, panelWidth, artHeight, panelGap) {
      const namespace = 'http://www.w3.org/2000/svg';
      const panels = Math.max(1, Number(size.panels) || 1);
      const frameSize = clamp(Math.min(panelWidth, artHeight) * .055, 2.5, 13);
      const fragment = document.createDocumentFragment();

      for (let index = 0; index < panels; index += 1) {
        const panelX = artX + index * (panelWidth + panelGap);
        const shadow = document.createElementNS(namespace, 'rect');
        shadow.setAttribute('class', 'codex-size-guide__scene-art-shadow');
        shadow.setAttribute('rx', '2');
        this.setRect(shadow, panelX - 3, artY + 7, panelWidth + 6, artHeight + 6);

        const frame = document.createElementNS(namespace, 'rect');
        frame.setAttribute('class', 'codex-size-guide__scene-art-frame');
        frame.setAttribute('rx', '2');
        this.setRect(frame, panelX, artY, panelWidth, artHeight);

        const fill = document.createElementNS(namespace, 'rect');
        fill.setAttribute('class', 'codex-size-guide__scene-art-fill');
        fill.setAttribute('rx', '1');
        fill.setAttribute('fill', this.artPatternFill);
        this.setRect(
          fill,
          panelX + frameSize,
          artY + frameSize,
          Math.max(4, panelWidth - frameSize * 2),
          Math.max(4, artHeight - frameSize * 2)
        );

        fragment.append(shadow, frame, fill);
        if (index === 0) {
          this.artShadow = shadow;
          this.artFrame = frame;
          this.artFill = fill;
        }
      }

      this.art.replaceChildren(fragment);
    }

    setRect(node, x, y, width, height) {
      if (!node) return;
      node.setAttribute('x', String(Math.round(x * 10) / 10));
      node.setAttribute('y', String(Math.round(y * 10) / 10));
      node.setAttribute('width', String(Math.round(width * 10) / 10));
      node.setAttribute('height', String(Math.round(height * 10) / 10));
    }

    setLine(node, x1, y1, x2, y2) {
      if (!node) return;
      node.setAttribute('x1', String(Math.round(x1 * 10) / 10));
      node.setAttribute('y1', String(Math.round(y1 * 10) / 10));
      node.setAttribute('x2', String(Math.round(x2 * 10) / 10));
      node.setAttribute('y2', String(Math.round(y2 * 10) / 10));
    }

    setText(node, x, y, text) {
      if (!node) return;
      node.setAttribute('x', String(Math.round(x * 10) / 10));
      node.setAttribute('y', String(Math.round(y * 10) / 10));
      node.textContent = text;
    }
  }

  customElements.define('codex-aassort-size-guide', CodexAassortSizeGuide);
})();

